// Zyla API Service for VIN decoding
// You'll need to get an API key from https://zylalabs.com/

const ZYLA_API_KEY = import.meta.env?.VITE_ZYLA_API_KEY;
const ZYLA_BASE_URL = 'https://zylalabs.com/api/7250/vin+information+lookup+api/11482/europe+vin+decoder'; //europe+vin+decoder';

class VinService {
  constructor() {
    this.apiKey = ZYLA_API_KEY;
    this.baseUrl = ZYLA_BASE_URL;
  }


  // Decode VIN using Zyla API
  async decodeVin(vin) {
    console.log('🚀 decodeVin called with VIN:', vin); // Debug log
    console.log('🔑 API Key configured:', !!this.apiKey); // Debug log
    
    try {
      if (!this.apiKey) {
        console.log('❌ No API key found'); // Debug log
        throw new Error('Zyla API key not configured. Please add VITE_ZYLA_API_KEY to your environment variables.');
      }

      // Clean and validate VIN
      const cleanVin = vin.replace(/\s/g, '').toUpperCase();
      console.log('🧹 Cleaned VIN:', cleanVin); // Debug log
      
      if (!this.validateVin(cleanVin)) {
        console.log('❌ VIN validation failed'); // Debug log
        throw new Error('Ungültiges VIN-Format. VIN muss 17 Zeichen lang sein und darf keine I, O oder Q enthalten.');
      }
      console.log('✅ VIN validation passed'); // Debug log

      // Build API URL with VIN as query parameter
      const url = `${this.baseUrl}?vin=${encodeURIComponent(cleanVin)}`;
      console.log('🌐 API URL:', url); // Debug log

      console.log('📡 Making API request...'); // Debug log
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('📡 API Response status:', response.status); // Debug log

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('VIN nicht gefunden in der Datenbank');
        } else if (response.status === 401) {
          throw new Error('Ungültiger API-Schlüssel');
        } else if (response.status === 429) {
          throw new Error('API-Limit erreicht. Bitte versuchen Sie es später erneut.');
        } else {
          throw new Error(`API-Fehler: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      console.log('Zyla API Response:', data); // Debug log
      
      if (!data || typeof data !== 'object') {
        throw new Error('Ungültige Antwort von der API');
      }

      const transformedData = this.transformZylaVinData(data, cleanVin);
      console.log('Transformed Data:', transformedData); // Debug log
      
      return transformedData;
    } catch (error) {
      console.error('VIN decoding error:', error);
      throw error;
    }
  }

  // Transform Zyla API response to our format
  transformZylaVinData(data, vin) {
    console.log('Transforming data:', data); // Debug log
    
    const vehicleData = {
      vin: data.VIN || vin,
      make: data.Make || '',
      model: data.Model || '',
      year: data['Model year'] || '',
      engine: data['Engine type'] || data['Engine'] || '',
      transmission: data.Transmission || '',
      fuelType: this.mapFuelType(data['Fuel type']),
      bodyType: this.mapBodyType(data['Body type'] || data['Body style']),
      color: 'Nicht angegeben',
      doors: data['Number of doors'] || 'N/A',
      displacement: data['Engine displacement'] ? `${data['Engine displacement']} ccm` : 'N/A',
      power: data['Engine power'] ? `${data['Engine power']} kW` : 'N/A',
      cylinders: data['Engine cylinders'] || 'N/A',
      driveType: data.Driveline || data['Drive type'] || 'N/A',
      trim: data.Trim || data['Trim level'] || 'N/A',
      series: data.Series || 'N/A',
      country: data.Country || data['Manufactured in'] || 'N/A',
      plant: data['Manufacturer'] || 'N/A',
      imageUrl: null,
      timestamp: new Date().toISOString(),
      source: 'Zyla API'
    };

    console.log('Final vehicle data:', vehicleData); // Debug log
    return vehicleData;
  }

  // Map fuel types to German
  mapFuelType(fuelType) {
    const fuelTypeMap = {
      'gasoline': 'Benzin',
      'diesel': 'Diesel',
      'electric': 'Elektro',
      'hybrid': 'Hybrid',
      'plug-in hybrid': 'Plug-in Hybrid',
      'hydrogen': 'Wasserstoff',
      'lpg': 'LPG',
      'cng': 'CNG',
      'ethanol': 'Ethanol',
      'biodiesel': 'Biodiesel'
    };
    return fuelTypeMap[fuelType?.toLowerCase()] || fuelType || 'N/A';
  }

  // Map body types to German
  mapBodyType(bodyType) {
    const bodyTypeMap = {
      'sedan': 'Limousine',
      'saloon': 'Limousine',
      'hatchback': 'Schrägheck',
      'wagon': 'Kombi',
      'estate': 'Kombi',
      'combi': 'Kombi',
      'suv': 'SUV',
      'coupe': 'Coupé',
      'convertible': 'Cabrio',
      'pickup': 'Pickup',
      'van': 'Van',
      'minivan': 'Minivan',
      'truck': 'LKW',
      'bus': 'Bus',
      'motorcycle': 'Motorrad',
      'mpv': 'MPV'
    };
    return bodyTypeMap[bodyType?.toLowerCase()] || bodyType || 'N/A';
  }

  // Validate VIN format
  validateVin(vin) {
    if (!vin) return false;
    
    // Remove spaces and convert to uppercase
    const cleanVin = vin.replace(/\s/g, '').toUpperCase();
    
    // Check length (VINs are typically 17 characters)
    if (cleanVin.length !== 17) {
      return false;
    }
    
    // Check for valid characters (no I, O, Q)
    const invalidChars = /[IOQ]/;
    if (invalidChars.test(cleanVin)) {
      return false;
    }
    
    return true;
  }

  // Extract VIN from image (OCR simulation - would need real OCR service)
  async extractVinFromImage(imageData) {
    // This is a placeholder for OCR functionality
    // In a real implementation, you would use an OCR service like:
    // - Google Cloud Vision API
    // - Azure Computer Vision
    // - AWS Textract
    // - Tesseract.js for client-side OCR
    
    try {
      // Simulate OCR processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For now, return null to indicate no VIN found
      // In a real implementation, this would return the extracted VIN
      return null;
    } catch (error) {
      console.error('OCR error:', error);
      throw new Error('Fehler beim Extrahieren des VIN-Codes aus dem Bild');
    }
  }

  // Cache VIN data in localStorage
  cacheVinData(vin, data) {
    try {
      const cache = JSON.parse(localStorage.getItem('vinCache') || '{}');
      cache[vin] = {
        data,
        timestamp: Date.now()
      };
      
      // Keep only last 100 cached entries
      const entries = Object.entries(cache);
      if (entries.length > 100) {
        const sortedEntries = entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
        const newCache = Object.fromEntries(sortedEntries.slice(0, 100));
        localStorage.setItem('vinCache', JSON.stringify(newCache));
      } else {
        localStorage.setItem('vinCache', JSON.stringify(cache));
      }
    } catch (error) {
      console.error('Cache error:', error);
    }
  }

  // Get cached VIN data
  getCachedVinData(vin) {
    try {
      const cache = JSON.parse(localStorage.getItem('vinCache') || '{}');
      const cached = cache[vin];
      
      if (cached) {
        // Check if cache is less than 24 hours old
        const cacheAge = Date.now() - cached.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (cacheAge < maxAge) {
          return cached.data;
        } else {
          // Remove expired cache entry
          delete cache[vin];
          localStorage.setItem('vinCache', JSON.stringify(cache));
        }
      }
      
      return null;
    } catch (error) {
      console.error('Cache retrieval error:', error);
      return null;
    }
  }
}

export default new VinService();
